import { useState } from 'react'
import { PlusIcon } from '@radix-ui/react-icons'
import { Flex } from '@radix-ui/themes'
import { DataTestIDs } from 'shared/constants'
import { NeonButton } from 'shared/ui/neon'
import { shortenAddress } from 'shared/utils/shorten-address'
import { WalletAppList } from './wallet-app-list'
import styles from '../neon-wallets.module.scss'

/** Connecting a linked wallet (`useConnectLinkedWallet`). */
export interface WalletConnectProps {
  /** The wallet app that holds the wallet here; null asks the user to pick. */
  walletApp: string | null
  isConnecting: boolean
  /** Another wallet is connecting. */
  isBusy: boolean
  onConnect: (address: string, walletName?: string) => void
}

interface Props {
  address: string
  onDeposit: (address: string) => void
  /** Offered while the wallet is not the one that plays. */
  connect?: WalletConnectProps
}

/** Which wallet app to connect a wallet with that this device has not seen yet. */
const WalletAppPicker: React.FC<{
  address: string
  isBusy: boolean
  onPick: (walletName: string) => void
  onCancel: () => void
}> = ({ address, isBusy, onPick, onCancel }) => (
  <Flex
    direction='column'
    gap='2'
  >
    <span className={styles.sectionText}>
      Which wallet app holds {shortenAddress(address)}?
    </span>
    <WalletAppList
      variant='tiles'
      isBusy={isBusy}
      onPick={provider => onPick(provider.walletName)}
      buttonTestId={DataTestIDs.buttonPickLinkedWalletApp}
    />
    <button
      type='button'
      className={styles.secondaryButton}
      onClick={onCancel}
    >
      Cancel
    </button>
  </Flex>
)

/**
 * Top up and, for a wallet that does not play, Connect: through the wallet
 * app known to hold it, or the app the user picks for it.
 */
export const WalletCardActions: React.FC<Props> = ({
  address,
  onDeposit,
  connect
}) => {
  const [isPickingApp, setIsPickingApp] = useState(false)

  if (connect && isPickingApp) {
    return (
      <WalletAppPicker
        address={address}
        isBusy={connect.isBusy || connect.isConnecting}
        onPick={walletName => {
          setIsPickingApp(false)
          connect.onConnect(address, walletName)
        }}
        onCancel={() => setIsPickingApp(false)}
      />
    )
  }

  return (
    <Flex
      gap='2'
      className={styles.cardActions}
    >
      <NeonButton
        type='button'
        iconLeft={<PlusIcon />}
        onClick={() => onDeposit(address)}
        data-testid={DataTestIDs.buttonWalletDeposit}
      >
        Top up
      </NeonButton>
      {connect ?
        <button
          type='button'
          className={styles.secondaryButton}
          disabled={connect.isBusy || connect.isConnecting}
          onClick={() =>
            connect.walletApp ?
              connect.onConnect(address, connect.walletApp)
            : setIsPickingApp(true)
          }
          data-testid={DataTestIDs.buttonConnectLinkedWallet}
        >
          {connect.isConnecting ? 'Connecting…' : 'Connect'}
        </button>
      : null}
    </Flex>
  )
}
